/**
 * ViewIR (`view_ir:1`) blueprint → a2ui (`view_ir:2`) `A2uiWidgetView` (DEC-6).
 *
 * Pure, deterministic, idempotent. This is what P6 runs per widget at PUBLISH
 * time (`wd-api upload --a2ui`) and what the studio drift panel + the render
 * parity tests exercise. The inverse (`a2uiSurfaceToBlueprint` in components.js)
 * reconstructs the blueprint for the web runtime, so forward+inverse are a
 * bijection on the shapes emitted here (parity by construction).
 *
 * Transformations (all deterministic):
 *   pages[].root subtree  → one `createSurface` per page (surfaceId `<widget_id>/<page>`)
 *   page root id          → `"root"`
 *   type                  → component (Vertical→Column, Horizontal→Row, Surface→Panel)
 *   inline card/cell/center/from/to / inline-node props → hoisted `<parent>__<prop>`
 *   children ids          → ChildList (array); `repeat` → ChildList template + @index
 *   dotted bind           → RFC-6901 (`{interp}`→formatString, `{agg}`→vi_agg, cycle/map/format→vi_*)
 *   when                  → visible (vi_<op>)
 *   connections           → on_<signal> ActionList (vi_deliver / vi_setData / host_*)
 *   connection targets    → metadata.extensions.vi_slots on the receiver
 */

import {
  WIDGETS_CATALOG_ID, WIDGET_PROTOCOL_VERSION, WIDGET_APP_MIN_VERSION,
  CATALOG_META, VIEWIR_TO_A2UI, canonicalComponent, validateComponentNode, targetFor,
} from './catalog.js';
import {
  bindToDynamic, whenToVisible, connectionToAction, hasInterpolation, interpToFormatString,
} from './functions.js';

const INLINE_PROP_KEYS = ['card', 'cell', 'center', 'from', 'to'];

/** True for a value that is an inline viewIr component node (`{ type: <known> }`). */
function isInlineNode(v) {
  return v && typeof v === 'object' && !Array.isArray(v) && typeof v.type === 'string'
    && (canonicalComponent(v.type) != null || VIEWIR_TO_A2UI[v.type] != null);
}

/**
 * Convert a `view_ir:1` blueprint. Options: `{ catalog }` = the full widgets
 * catalog JSON for strict prop validation (optional; the bundled meta is used
 * otherwise), `{ appMinVersion }` (default 0.10.0). Throws on an unknown
 * component or an unknown prop (fail loud) unless `{ lax:true }`.
 */
export function convertBlueprint(blueprint, options = {}) {
  if (!blueprint || typeof blueprint !== 'object') throw new Error('convertBlueprint: not an object');
  // idempotency guard: an already-converted view passes through unchanged
  if (Number(blueprint.view_ir) >= 2 || blueprint.format === 'a2ui/1.0') {
    return structuredClone(blueprint);
  }

  const components = blueprint.components || {};
  const pages = Array.isArray(blueprint.pages) ? blueprint.pages : [];
  const connections = Array.isArray(blueprint.connections) ? blueprint.connections : [];

  // precompute per-component signal handlers + inbound slots from connections
  const onSignals = {}; // compId → { signal → [actions] }
  const viSlots = {}; // compId → Set(slot)
  for (const conn of connections) {
    if (!conn || typeof conn.from !== 'string' || typeof conn.to !== 'string') continue;
    const src = conn.from.split('.')[0];
    const { signal, action } = connectionToAction(conn);
    (onSignals[src] ||= {});
    (onSignals[src][signal] ||= []).push(action);
    const [tComp, tSlot] = [conn.to.split('.')[0], conn.to.split('.').slice(1).join('.')];
    if (tComp && tComp !== 'host' && tComp !== 'page_args' && tSlot) {
      (viSlots[tComp] ||= new Set()).add(tSlot);
    }
  }

  const errors = [];
  const stats = { pages: 0, components: 0, byKind: { core: 0, 'core-extension': 0, panel: 0, custom: 0, composed: 0 }, hoisted: 0, functionsUsed: new Set() };
  const surfaces = [];
  const targetForm = options.targetForm === true;
  const synthSeq = { n: 0 }; // unique ids for target-form expanded core children

  for (const page of pages) {
    const rootId = page.root;
    if (!rootId || !components[rootId]) continue;
    stats.pages += 1;
    const out = [];
    const seen = new Set();
    const queue = [{ id: rootId, isRoot: true }];
    while (queue.length) {
      const { id, isRoot, inlineNode } = queue.shift();
      if (seen.has(id)) continue;
      seen.add(id);
      const node = inlineNode || components[id];
      if (!node) continue;
      const { comp, extraQueue, synthetic } = convertNode(id, node, { isRoot, onSignals, viSlots, stats, errors, components, catalog: options.catalog, targetForm, synthSeq });
      out.push(comp);
      if (synthetic) for (const s of synthetic) out.push(s); // target-form expanded core children
      for (const q of extraQueue) queue.push(q);
      // ItemPage/SessionPage/region-routed nodes reference sub-components by id
      // in their PROPS (not a `children` array), so scan every prop string for a
      // component-id ref and pull it into the surface. Over-inclusion is safe:
      // ViewIRPage only renders what the page root reaches.
      for (const ref of idRefsInNode(node, components)) if (!seen.has(ref)) queue.push({ id: ref });
    }

    // HOST-INBOUND wires (`from: "host.<signal>"`). `onSignals` is keyed by the
    // SOURCE component id, and `host` is not a component — so these were built
    // and then attached to nothing, and every one of them was dropped: 35 across
    // the nine widgets, including `host.dataReceived → <page>.applyUpdate` in ALL
    // of them. That is the wire that pushes new data into a live page, so a card
    // never appeared when its session started and never updated in place.
    // They belong to the SURFACE, not to any component, so they ride on the page
    // root's metadata rather than as a root `on_*` — which would be
    // indistinguishable from a signal the root itself emits.
    // `connections` is blueprint-GLOBAL, so these ride once — on the entry
    // surface's root. Attaching them to every surface whose root could hold them
    // re-emitted the same wire per page, and the inverse then built a blueprint
    // with more connections than it started with.
    const hostSignals = onSignals.host;
    if (page.entry === true && hostSignals && Object.keys(hostSignals).length) {
      const rootComp = out.find((c) => c.id === 'root');
      if (rootComp) setExt(rootComp, 'vi_host_on', hostSignals);
    }

    surfaces.push({
      page: String(page.id),
      entry: page.entry === true,
      // The bundle file name the HOST routes by. It is not derivable from the
      // page id (`detail` is served as `data_view.html`), so dropping it here
      // makes every non-entry page unreachable after reconstruction: the kits
      // resolve an incoming `entryFile` through `pageForFile`, which matches on
      // this exact string. Kept OUTSIDE `createSurface` — that object is the
      // literal a2ui message and is `additionalProperties:false`; `page` and
      // `entry` already live at this envelope level.
      // A NULL page_file is a real, deliberate value — `detail` pages ship with
      // none, and the kit then defaults them to the entry file. Emitting the key
      // ONLY when set made "deliberately null" indistinguishable from "converted
      // before this field existed", so the inverse's legacy fallback invented
      // `<page>.html` for them and the SAME page routed to a different file on
      // the two forms. Always emitting the key — null included — makes the round
      // trip lossless and keeps the fallback for genuinely old documents only.
      page_file: page.page_file == null ? null : String(page.page_file),
      createSurface: {
        surfaceId: `${blueprint.widget_id}/${page.id}`,
        catalogId: WIDGETS_CATALOG_ID,
        components: out,
      },
    });
  }

  if (errors.length && !options.lax) {
    throw new Error(`convertBlueprint validation failed:\n  ${errors.join('\n  ')}`);
  }

  const view = {
    view_ir: 2,
    format: 'a2ui/1.0',
    protocolVersion: WIDGET_PROTOCOL_VERSION,
    catalogId: WIDGETS_CATALOG_ID,
    widget_id: blueprint.widget_id,
    title: blueprint.title ?? '',
    app_min_version: options.appMinVersion || WIDGET_APP_MIN_VERSION,
    // The AUTHORED floor, carried so conversion cannot move it.
    //
    // `app_min_version` above is the SERVING floor: an a2ui row must declare
    // >= 0.10.0 or the commit guard rejects it, which is what makes the v133
    // cohort split safe. But the kit ALSO reads this field as a LAYOUT switch —
    // `viewIrBodyHorizontalInset` gives a blueprint that declares >= 0.9.7 no
    // page gutter, on the theory that it draws its own. Migrating a widget
    // authored at 0.9.0 therefore stripped the gutter the kit had always drawn
    // for it, and every row ran edge to edge. Two different questions were
    // reading one field; this preserves the one the layout is really asking.
    ...(blueprint.app_min_version == null
      ? {}
      : { vi_authored_app_min_version: String(blueprint.app_min_version) }),
    surfaces,
    data: blueprint.data && typeof blueprint.data === 'object' ? structuredClone(blueprint.data) : {},
  };
  if (blueprint.accent != null) view.accent = blueprint.accent;
  // `contracts` is BACKEND-READ, not a kit concern — the Job-by-Contract mint reads the
  // PINNED blueprint (`job_contract_wiring._load_blueprint_view_json`) to learn what a
  // launch owes: the job chain, its `then[]` order, the in_progress anchor's type, and
  // whether the door owes a SINGLE row. Dropping it on migration silently un-declared
  // every chain for the a2ui cohort — scene-studio's image→video→log became the generic
  // "one widget_log" the moment its row was converted, and no render check can see it.
  if (blueprint.contracts && typeof blueprint.contracts === 'object') {
    view.contracts = structuredClone(blueprint.contracts);
  }
  // stats ride as a NON-enumerable property so they never pollute the canonical
  // view (JSON.stringify / structuredClone / deep-equal all ignore them).
  Object.defineProperty(view, '_stats', {
    value: { ...stats, byKind: { ...stats.byKind }, functionsUsed: [...stats.functionsUsed].sort() },
    enumerable: false,
  });
  return view;
}

function convertNode(id, node, ctx) {
  const { isRoot, onSignals, viSlots, stats, errors, catalog, targetForm, synthSeq } = ctx;
  const canon = canonicalComponent(node.type) || VIEWIR_TO_A2UI[node.type] || node.type;
  const meta = CATALOG_META[canon];
  let comp = { id: isRoot ? 'root' : id, component: canon };
  const extraQueue = [];
  let synthetic;

  // props (deep bind conversion + inline-node hoisting)
  const props = node.props && typeof node.props === 'object' ? node.props : {};
  for (const [k, v] of Object.entries(props)) {
    if (isInlineNode(v) || INLINE_PROP_KEYS.includes(k) && isInlineNode(v)) {
      const hid = `${id}__${k}`;
      extraQueue.push({ id: hid, inlineNode: v });
      comp[k] = hid; // ComponentId reference
      stats.hoisted += 1;
    } else {
      comp[k] = convertValue(v, stats);
    }
  }

  // core prop renames (value→text, src→url, alt→description, poster→posterUrl)
  applyCoreRenames(canon, comp);

  // node-level bind → the content prop (typed core), metadata.vi_bind (other
  // typed components have no `bind` prop), or an opaque `bind` (untyped custom).
  if (node.bind != null) {
    const dyn = bindToDynamic(node.bind);
    recordFn(dyn, stats);
    const target = BIND_TARGET[canon];
    if (target) comp[target] = dyn;
    else if (meta && meta.typed) setExt(comp, 'vi_bind', dyn);
    else comp.bind = dyn;
  }

  // when → visible
  if (node.when != null) {
    const vis = whenToVisible(node.when);
    if (vis !== undefined) { comp.visible = vis; recordFn(vis, stats); }
  }

  // children (id array or inline nodes) or a repeat template
  const childOut = convertChildren(id, node, extraQueue);
  if (childOut !== undefined) comp.children = childOut;

  // variant — a real prop where the catalog has one, else metadata
  if (node.variant != null) {
    if (meta && (meta.props.includes('variant') || !meta.typed)) comp.variant = node.variant;
    else setExt(comp, 'vi_variant', node.variant);
  }
  if (node.region != null && comp.region == null) comp.region = node.region;

  // connection-derived behavior + slots
  const signals = onSignals[id];
  if (signals) {
    for (const [sig, actions] of Object.entries(signals)) {
      comp[`on_${sig}`] = actions;
      for (const a of actions) recordFn(a.functionCall, stats);
    }
  }
  const slots = viSlots[id];
  if (slots && slots.size) setExt(comp, 'vi_slots', [...slots].sort());

  // a TYPED component may only carry catalog props at the top level; a viewIr
  // prop the a2ui catalog does not model rides under metadata.extensions.vi_props
  // (round-trippable) rather than failing validation.
  if (meta && meta.typed) {
    const allowed = new Set([...meta.props, 'id', 'component', 'metadata', 'children']);
    const stray = {};
    for (const k of Object.keys(comp)) {
      if (allowed.has(k) || /^on_[A-Za-z0-9_]+$/.test(k)) continue;
      stray[k] = comp[k];
    }
    for (const k of Object.keys(stray)) delete comp[k];
    if (Object.keys(stray).length) setExt(comp, 'vi_props', stray);
  }

  // widgets-formalize TARGET FORM (flag-gated): drop supersets/customs to a core
  // host that is RENDERABLE ON ITS OWN (core props + expanded core children), with
  // `metadata.extensions.collov_*` (core-extension) or `vi_origin` (pure-core drop)
  // carrying the full payload so OUR kit re-keys to the origin renderer (render
  // parity by construction). A strict third-party that ignores every extension
  // still gets the core tree. The default (unflagged) form is byte-identical.
  if (targetForm) {
    const wrapped = wrapAsTarget(comp, canon, () => `${id}__t${synthSeq.n++}`);
    comp = wrapped.host;
    synthetic = wrapped.synthetic;
  }

  // stats + validation
  stats.components += 1;
  const statKind = targetForm ? targetFor(canon).kind : (meta ? meta.kind : 'custom');
  stats.byKind[statKind] = (stats.byKind[statKind] || 0) + 1;
  const errs = validateComponentNode(comp, catalog);
  for (const e of errs) errors.push(`[${id}] ${e}`);
  for (const s of synthetic || []) for (const e of validateComponentNode(s, catalog)) errors.push(`[${s.id}] ${e}`);

  return { comp, extraQueue, synthetic };
}

/**
 * Rewrite a fully-formalised a2ui node into its widgets-formalize TARGET FORM.
 *
 * The host is a CORE component that renders on its own: core props (Text.text,
 * Image.url/description, Button.label/action, Divider.axis, container children …)
 * plus EXPANDED core children generated from the origin's payload for the customs
 * whose content lives in props (StatTileRow → Row[Card[Text,Text]], KeyValueList →
 * List[Row[Text,Text]], Timeline → List[Row[Text,Text]], EmptyState → Card[Column[
 * Icon,Text,Text]], …). The FULL formalised payload also rides in the extension —
 * `collov_*` for a core-extension (origin = the key, via EXTENSION_TO_COMPONENT)
 * or `vi_origin` for a pure-core drop — so OUR kit re-keys it back to the origin
 * renderer (exact parity), while a strict third-party that strips every extension
 * still gets the core tree. `mkId` mints unique ids for the synthetic children.
 *
 * Idempotent-safe: a true custom or an already-core self-host is returned as-is.
 */
function wrapAsTarget(comp, canon, mkId) {
  const t = targetFor(canon);
  if (t.kind === 'custom') return { host: comp, synthetic: [] }; // true custom — unchanged
  if (t.host === canon && !t.extension) return { host: comp, synthetic: [] }; // already a core self-host

  // the full formalised node (minus id/component) → the extension payload; re-key
  // reads its `children` (the ORIGIN's), so nothing is lost for our kit.
  const payload = {};
  for (const k of Object.keys(comp)) { if (k === 'id' || k === 'component') continue; payload[k] = comp[k]; }

  const host = { id: comp.id, component: t.host };
  const rep = coreRepresentation(canon, t.host, comp, mkId);
  Object.assign(host, rep.props);
  if (rep.children !== undefined) host.children = rep.children;

  const wrapKey = t.extension || 'vi_origin';
  host.metadata = { extensions: { [wrapKey]: t.extension ? payload : { component: canon, ...payload } } };
  return { host, synthetic: rep.synthetic };
}

/**
 * The core `action` of a target-form Button: what a STRICT third-party renderer
 * runs on press. A pure browser-side op maps to its core twin (`host_openUrl` →
 * core `openUrl`); every other origin handler (host_openSession, host_openCapture,
 * vi_deliver, vi_setData, …) becomes an agent-side `event` named after the call,
 * its args as the event context (paths stay live DynamicValues), so the press
 * reaches the agent API as a standard `action` message instead of dying on a
 * static button. Only the FIRST `on_activated` entry maps (core Action is one
 * handler; its `when` guard is not expressible there) — the full list rides in
 * the extension payload for our kits. A handler-less button emits `activated`.
 */
function coreButtonAction(comp) {
  const a = comp.action;
  if (a && typeof a === 'object' && (a.event || a.functionCall)) return a; // already core
  const first = Array.isArray(comp.on_activated) ? comp.on_activated.find((x) => x && x.functionCall) : undefined;
  const call = first ? String(first.functionCall.call || '') : '';
  const args = first && first.functionCall.args && typeof first.functionCall.args === 'object' ? first.functionCall.args : {};
  if (call === 'host_openUrl' && args.url !== undefined) return { functionCall: { call: 'openUrl', args: { url: args.url } } };
  if (!call) return { event: { name: 'activated', context: { id: comp.id } } };
  return Object.keys(args).length ? { event: { name: call, context: args } } : { event: { name: call } };
}

function firstDefined(...vals) { for (const v of vals) if (v != null && v !== '') return v; return undefined; }
function asText(v) {
  if (v == null) return undefined;
  if (typeof v === 'object') return v.text ?? v.value ?? v.label ?? v.title ?? undefined; // a Dynamic/formatString rides through as-is
  return v;
}
function pick(comp, keys) {
  const o = {};
  for (const k of keys) if (comp[k] !== undefined) o[k] = comp[k];
  return o;
}
/** A synthetic core Text node. */
function synthText(mkId, value, variant) {
  const c = { id: mkId(), component: 'Text' };
  if (value !== undefined) c.text = value;
  if (variant) c.variant = variant;
  return c;
}

/**
 * The CORE representation of an origin custom on its core host: `props` set on the
 * host, `children` (ids), and any `synthetic` core components those ids point to.
 * Content that lives in a `.items`/`.tabs`/`.options` prop is expanded into a core
 * subtree so a third-party sees it; everything is best-effort and lossy-styling.
 */
function coreRepresentation(canon, host, comp, mkId) {
  const synthetic = [];
  const add = (c) => { synthetic.push(c); return c.id; };
  const items = Array.isArray(comp.items) ? comp.items : null;

  // ── containers: children pass through (List gets a direction) ──────────────
  if (host === 'Column' || host === 'Row' || host === 'List') {
    const props = {};
    if (host === 'List') props.direction = canon === 'Carousel' ? 'horizontal' : (comp.direction || 'vertical');
    if (comp.align !== undefined) props.align = comp.align;

    // items-bearing customs → a core subtree per item (value/label rows or cards)
    if (items && !comp.children) {
      const children = items.map((it) => {
        if (canon === 'StatTileRow') {
          const col = add({ id: mkId(), component: 'Column', children: [
            add(synthText(mkId, firstDefined(asText(it.value), asText(it)), 'h3')),
            add(synthText(mkId, firstDefined([asText(it.label), asText(it.unit)].filter(Boolean).join(' ')), 'caption')),
          ] });
          return add({ id: mkId(), component: 'Card', children: [col] });
        }
        if (canon === 'NoticeList') {
          const col = add({ id: mkId(), component: 'Column', children: [
            add(synthText(mkId, firstDefined(asText(it.title), asText(it)), 'body')),
            ...(it.detail != null ? [add(synthText(mkId, asText(it.detail), 'caption'))] : []),
          ] });
          return add({ id: mkId(), component: 'Card', children: [col] });
        }
        // KeyValueList / Timeline / generic list → a Row of two Texts
        const a = firstDefined(asText(it.key), asText(it.title), asText(it.label), asText(it));
        const b = firstDefined(asText(it.value), asText(it.detail), asText(it.desc));
        return add({ id: mkId(), component: 'Row', children: [
          add(synthText(mkId, a, 'body')),
          ...(b !== undefined ? [add(synthText(mkId, b, 'caption'))] : []),
        ] });
      });
      return { props, children, synthetic };
    }
    return { props, children: comp.children, synthetic };
  }

  // ── Text-family leaves (keep the formalised value verbatim — it may be an a2ui
  //    Dynamic bind/formatString, which core Text renders) ─────────────────────
  if (host === 'Text') {
    const variant = comp.variant || (canon === 'SectionHeader' ? 'h3' : undefined);
    const text = firstDefined(comp.text, comp.value, comp.title, comp.label, '');
    return { props: { text, ...(variant ? { variant } : {}) }, synthetic };
  }
  if (host === 'Image') return { props: pick(comp, ['url', 'description', 'fit', 'ratio', 'variant']), synthetic };
  if (host === 'Video') return { props: pick(comp, ['url', 'posterUrl']), synthetic };
  if (host === 'Divider') return { props: { axis: comp.axis || 'horizontal' }, synthetic };
  if (host === 'Icon') return { props: pick(comp, ['name']), synthetic };
  if (host === 'Button') {
    // core Button = { child (a Text), action } — both REQUIRED by the basic catalog;
    // `label`/`children`/our variants are not core props (they stay in the payload).
    // a BOUND label (path / formatString) is a Dynamic core Text renders verbatim
    const raw = firstDefined(comp.label, comp.text);
    const label = raw && typeof raw === 'object' && ('path' in raw || 'call' in raw) ? raw : (firstDefined(asText(comp.label), asText(comp.text)) ?? '');
    const child = typeof comp.child === 'string' ? comp.child : add(synthText(mkId, label, 'body'));
    const variant = comp.variant === 'primary' ? 'primary' : (comp.variant === 'borderless' || comp.variant === 'link' ? 'borderless' : undefined);
    const props = { child, action: coreButtonAction(comp) };
    if (variant) props.variant = variant;
    if (comp.weight !== undefined) props.weight = comp.weight;
    return { props, synthetic };
  }
  if (host === 'TextField') return { props: pick(comp, ['label', 'value', 'placeholder', 'variant']), synthetic };
  if (host === 'ChoicePicker') {
    return { props: { ...pick(comp, ['options', 'label', 'variant', 'displayStyle']), ...(comp.selected !== undefined ? { value: comp.selected } : {}) }, synthetic };
  }
  if (host === 'Tabs') {
    const tabs = Array.isArray(comp.tabs) ? comp.tabs.map((t) => ({ title: firstDefined(asText(t.title), asText(t.label), asText(t)) })) : undefined;
    return { props: tabs ? { tabs } : {}, synthetic };
  }
  if (host === 'Modal') return { props: pick(comp, ['trigger', 'content']), children: comp.children, synthetic };

  // ── Card-family: a Card whose children carry the media + title/subtitle + body
  if (host === 'Card') {
    const kids = [];
    // media (a bound image on our custom Card) → a core Image child, so a
    // third-party still gets the picture (bound → paints when the row arrives)
    const media = comp.media;
    if (media !== undefined) {
      const url = media && typeof media === 'object' ? firstDefined(media.src, media.url) : media;
      if (url !== undefined) {
        const img = { id: mkId(), component: 'Image', url };
        const desc = firstDefined(asText(comp.title), asText(comp.eyebrow), media && media.alt);
        if (desc !== undefined) img.description = desc;
        if (media && media.ratio) img.ratio = media.ratio;
        kids.push(add(img));
      }
    }
    const title = firstDefined(asText(comp.title), asText(comp.eyebrow));
    if (title !== undefined) kids.push(add(synthText(mkId, title, 'h3')));
    const sub = firstDefined(asText(comp.subtitle), asText(comp.body));
    if (sub !== undefined) kids.push(add(synthText(mkId, sub, 'caption')));
    if (Array.isArray(comp.children)) kids.push(...comp.children);
    return { props: {}, children: kids.length ? kids : comp.children, synthetic };
  }

  // fallback: keep whatever children the node had
  return { props: {}, children: comp.children, synthetic };
}

/** viewIr node types whose node-level `bind` maps to a specific a2ui content prop. */
const BIND_TARGET = { Text: 'text', Image: 'url', Video: 'url' };

function applyCoreRenames(canon, comp) {
  if (canon === 'Text' && 'value' in comp && !('text' in comp)) { comp.text = comp.value; delete comp.value; }
  if (canon === 'Image') {
    if ('src' in comp && !('url' in comp)) { comp.url = comp.src; delete comp.src; }
    if ('alt' in comp && !('description' in comp)) { comp.description = comp.alt; delete comp.alt; }
  }
  if (canon === 'Video') {
    if ('src' in comp && !('url' in comp)) { comp.url = comp.src; delete comp.src; }
    if ('poster' in comp && !('posterUrl' in comp)) { comp.posterUrl = comp.poster; delete comp.poster; }
  }
}

function convertChildren(id, node, extraQueue) {
  // repeat: the node itself becomes a ChildList template over an array path.
  // The ROW BODY is this node's children, and it has to be EMITTED as the
  // template component — naming `<id>__item` without queueing it left a dangling
  // ChildList reference, so the repeated section reconstructed with no children
  // and rendered nothing at all.
  if (node.repeat != null) {
    const path = bindPath(node.repeat);
    const kids = Array.isArray(node.children) ? node.children : [];
    if (kids.length === 1 && typeof kids[0] === 'string') {
      // the body is an existing component — reference it directly
      extraQueue.push({ id: kids[0] });
      return { componentId: kids[0], path };
    }
    const hid = `${id}__item`;
    if (kids.length === 1 && isInlineNode(kids[0])) {
      extraQueue.push({ id: hid, inlineNode: kids[0] });
    } else if (kids.length > 1) {
      // more than one body node needs a single template root to hang them from
      extraQueue.push({ id: hid, inlineNode: { type: 'Vertical', props: {}, children: kids } });
    } else {
      return { componentId: hid, path }; // nothing to template (unchanged)
    }
    return { componentId: hid, path };
  }
  const children = node.children;
  if (children == null) return undefined;
  if (!Array.isArray(children)) return undefined;
  return children.map((child, i) => {
    if (typeof child === 'string') { extraQueue.push({ id: child }); return child; }
    if (isInlineNode(child)) {
      const hid = `${id}__child${i}`;
      extraQueue.push({ id: hid, inlineNode: child });
      return hid;
    }
    return String(child);
  });
}

/** Every component-id referenced by a string anywhere in a node's props/children. */
function idRefsInNode(node, components) {
  const out = new Set();
  const walk = (v) => {
    if (typeof v === 'string') { if (Object.prototype.hasOwnProperty.call(components, v)) out.add(v); return; }
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (v && typeof v === 'object') { for (const [k, x] of Object.entries(v)) { if (k === 'type') continue; walk(x); } }
  };
  walk(node.props);
  walk(node.children);
  return out;
}

function bindPath(expr) {
  const dyn = bindToDynamic(expr);
  return dyn && dyn.path ? dyn.path : (typeof expr === 'string' ? '/' + expr : '/');
}

/**
 * Deep-convert a prop VALUE. viewIr prop strings are LITERALS by default — only a
 * `{interp}` string becomes a formatString and only an explicit bind object
 * (`{$}`/`{bind}`/`{agg}`) becomes a data read. A bare identifier (`"tops"`,
 * `"Top"`, `"manual"`) is a literal, never a path.
 */
function convertValue(v, stats) {
  if (v == null) return v;
  if (typeof v === 'string') {
    if (hasInterpolation(v)) { const dyn = interpToFormatString(v); recordFn(dyn, stats); return dyn; }
    return v; // literal
  }
  if (Array.isArray(v)) return v.map((x) => convertValue(x, stats));
  if (typeof v === 'object') {
    if ('$' in v || 'bind' in v || 'agg' in v) {
      const dyn = bindToDynamic(v);
      recordFn(dyn, stats);
      return dyn;
    }
    const out = {};
    for (const [k, val] of Object.entries(v)) out[k] = convertValue(val, stats);
    return out;
  }
  return v;
}

function recordFn(dyn, stats) {
  if (dyn && typeof dyn === 'object') {
    if (typeof dyn.call === 'string') stats.functionsUsed.add(dyn.call);
    for (const v of Object.values(dyn.args || {})) recordFn(v, stats);
  }
}

function setExt(comp, key, value) {
  comp.metadata ||= {};
  comp.metadata.extensions ||= {};
  comp.metadata.extensions[key] = value;
}

/** Convenience: validate a whole converted view, returning the error list. */
export function validateA2uiView(view, catalog = null) {
  const errors = [];
  for (const s of view.surfaces || []) {
    const comps = s.createSurface?.components || [];
    const roots = comps.filter((c) => c.id === 'root');
    if (roots.length !== 1) errors.push(`surface ${s.createSurface?.surfaceId}: expected exactly one root, got ${roots.length}`);
    for (const c of comps) for (const e of validateComponentNode(c, catalog)) errors.push(`${s.page}/${c.id}: ${e}`);
  }
  return errors;
}
